import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

const createOrgFormSchema = z.object({
	name: z.string().min(2, { message: "Organization name must be at least 2 characters" }),
});

type CreateOrgFormValues = z.infer<typeof createOrgFormSchema>;

interface CreateOrganizationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

export const CreateOrganizationModal = ({ isOpen, onClose, onSuccess }: CreateOrganizationModalProps) => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const form = useForm<CreateOrgFormValues>({
		resolver: zodResolver(createOrgFormSchema),
		defaultValues: {
			name: "",
		},
	});

	const handleSubmit = async (values: CreateOrgFormValues) => {
		if (isSubmitting) return;
		setIsSubmitting(true);

		try {
			const { data: newOrg, error: orgError } = await supabase
				.from('organizations')
				.insert([{ name: values.name }])
				.select('id')
				.single();

			if (orgError) throw orgError;

			const { error: subError } = await supabase
				.from('organization_subscription')
				.insert([{
					id: newOrg.id,
					subscription_issue: 'subscription_canceled',
					subscription_issue_since: new Date().toISOString(),
				}]);

			if (subError) throw subError;

			toast({
				title: t("common.success"),
				description: t("admin.modals.createOrg.success"),
			});

			form.reset();
			onSuccess();
			onClose();
		} catch (error: any) {
			toast({
				title: t("common.error"),
				description: error.message || t("admin.modals.createOrg.error"),
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>{t("admin.modals.createOrg.title")}</DialogTitle>
					<DialogDescription>
						{t("admin.modals.createOrg.description")}
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("admin.modals.createOrg.name")}</FormLabel>
									<FormControl>
										<Input placeholder={t("admin.modals.createOrg.namePlaceholder")} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="flex justify-end space-x-3 pt-4">
							<Button type="button" variant="outline" onClick={onClose}>
								{t("common.cancel")}
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{t("admin.modals.createOrg.submit")}
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};
